import {
  Box,
  Card,
  Flex,
  Form,
  Icon,
  Text,
  Button,
  Heading,
  CardBody,
  Textarea,
  IconButton,
  FormControl,
  FormErrorMessage,
  FormLabel,
  RadioGroup,
  Stack,
  Radio,
  Input,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { selectFullName, selectCustomerIsLoanOfficer } from '../../../../store/selectors/customerProfile'
import { selectPreferredRealEstateAgentMembership } from '../../../../store/selectors/clientTeamMemberships'
import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../store/hooks'
import useViewport from '../../../../hooks/useViewport'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { flashNotification } from '../../../../actions/notification'
import { selectClientId } from '../../../../store/selectors/client'
import { HOME_SEARCH_LISTING_DETAILS } from '../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { MessageTopicKey } from '../../../../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from '../../../../hooks/listings/useSendListingMessage'

import { hasCustomerToken } from '../../../../auth'
import { createTeamMembership } from '../../../../store/slices/homebotNetwork/clientTeamMemberships'
import { useVerificationFlowEnabled } from '../../../../hooks/useVerificationFlowEnabled'
import { useSafeVerificationFlow } from '../../../shared/Questions/hooks/useSafeVerificationFlow'
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'

export const MSG = defineMessages({
  email: {
    id: 'AgentCard.email',
    defaultMessage: 'Email'
  },
  firstName: {
    id: 'FormField.label.firstName',
    defaultMessage: 'First Name'
  },
  lastName: {
    id: 'Form.placeholder.lastName',
    defaultMessage: 'Last Name'
  },
  whoDoYouWantToAsk: {
    id: 'ListingDetails.ListingAgentCard.whoDoYouWantToAsk',
    defaultMessage: 'Who do you want to ask?'
  },
  addCustomAgentNote: {
    id: 'ListingDetails.ListingAgentCard.addCustomAgentNote',
    defaultMessage: "Send your questions to agent you know. We'll save this info as an option for future questions."
  },
  addCustomAgent: {
    id: 'ListingDetails.ListingAgentCard.addCustomAgent',
    defaultMessage: 'Add a preferred agent'
  },
  ask: {
    id: 'ListingDetails.ListingAgentCard.ask',
    defaultMessage: 'Ask listing agent, {name}'
  },
  head: {
    id: 'ListingDetails.ListingAgentCard.head',
    defaultMessage: 'Have a question?'
  },
  fail: {
    id: 'ListingDetails.ListingAgentCard.fail',
    defaultMessage: 'Message failed to send'
  },
  submit: {
    id: 'ListingDetails.ListingAgentCard.submit',
    defaultMessage: 'Send message'
  },
  label: {
    id: 'ListingDetails.ListingAgentCard.label',
    defaultMessage: 'Your question'
  },
  required: {
    id: 'ListingDetails.ListingAgentCard.required',
    defaultMessage: 'Required'
  },
  agent: {
    id: 'ListingDetails.ListingAgentCard.listAgent',
    defaultMessage: 'Listing agent'
  },
  rea: {
    id: 'ListingDetails.ListingAgentCard.rea',
    defaultMessage: 'Real estate agent'
  },
  show: {
    id: 'ListingDetails.ListingAgentCard.show',
    defaultMessage: 'Show agent contact card'
  },
  hide: {
    id: 'ListingDetails.ListingAgentCard.hide',
    defaultMessage: 'Hide agent contact card'
  },
  sub: {
    id: 'ListingDetails.ListingAgentCard.subhead',
    defaultMessage: 'We connect you directly to the listing agent who knows the property best. No cold calls or spam!'
  },
  footer: {
    id: 'ListingDetails.ListingAgentCard.footer',
    defaultMessage:
      "By clicking 'Send a Message' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email address) and any other information you include in your question with the listing agent identified above. You agree that the listing agent may may contact you about your inquiry."
  },
  sendMsgImpersonating: {
    id: 'ListingDetails.ListingAgentCard.sendMsgImpersonation',
    defaultMessage: 'Sending a message on behalf of your clients is not supported at this time.'
  },
  messageSent: {
    id: 'ListingDetails.ListingAgentCard.messageSent',
    defaultMessage: 'Message sent!'
  }
})

export interface NoPreferredAgentCardProps {
  listing?: Listing
}

export interface AgentOptions {
  type?: string
  email?: string | null
  name?: string | null
  company?: string | null
}

export interface CustomPreferredAgent {
  firstName?: string
  lastName?: string
  email?: string
}

export interface MessageFormValues extends CustomPreferredAgent {
  question: string
  preferredAgent?: string | null
}

export const NoPreferredAgentCard = ({ listing }: NoPreferredAgentCardProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const appDispatch = useAppDispatch()
  const isCustomerLo = useSelector(selectCustomerIsLoanOfficer)
  const clientId = useSelector(selectClientId) as string
  const { isLargeOrAbove } = useViewport()
  const [isHidden, setIsHidden] = useState(false)
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const { startVerification } = useSafeVerificationFlow()

  const preferredRealEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)

  const toast = useToast()
  const isImpersonating = hasCustomerToken()

  const hasPreferredAgent = !!preferredRealEstateAgentMembership?.customerProfile?.email

  const [selectedAgentEmail, setSelectedAgentEmail] = useState<string | undefined | null>(listing?.agent?.email)
  const [customPreferredAgent, setCustomPreferredAgent] = useState<CustomPreferredAgent>({})
  const [emailIsValid, setEmailIsValid] = useState(true)

  const sendToListingAgent = selectedAgentEmail !== listing.agent?.email
  const topic = sendToListingAgent
    ? MessageTopicKey.ListingsDirectMessageToListingAgent
    : MessageTopicKey.ListingsDirectMessageToHomebotNetworkPreferredAgent

  useEffect(() => {
    if (hasPreferredAgent) setSelectedAgentEmail(preferredRealEstateAgentMembership?.customerProfile?.email)
  }, [hasPreferredAgent, preferredRealEstateAgentMembership?.customerProfile])

  useEffect(() => {
    if (isLargeOrAbove) {
      setIsHidden(false)
    } else {
      setIsHidden(true)
    }
  }, [isLargeOrAbove])

  const { mutateAsync: sendListingMessage, isLoading: isSendingListingMessage } = useSendListingMessage()

  const updateAgent = async () => {
    let updated = true

    try {
      if (selectedAgentEmail === 'custom') {
        if (customPreferredAgent?.email && customPreferredAgent?.firstName) {
          await appDispatch(
            createTeamMembership({
              clientId,
              preferredAgentFirstName: customPreferredAgent?.firstName,
              preferredAgentLastName: customPreferredAgent?.lastName,
              preferredAgentEmail: customPreferredAgent?.email,
              isVerified: true,
              type: 'RealEstateAgent' // TODO: this will have to be updated when we do these for title
            })
          ).unwrap()
        }
      }
    } catch (error) {
      updated = false
    }

    return updated
  }
  //  @ts-ignore
  const handleSubmit = async (data: MessageFormValues) => {
    if (isImpersonating) {
      return toast({
        description: intl.formatMessage(MSG.sendMsgImpersonating),
        status: 'warning',
        position: 'top'
      })
    }
    const agentUpdated = await updateAgent()

    if (!agentUpdated || !listing?.id) {
      return dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }

    try {
      await sendListingMessage({
        input: {
          message: data.question,
          listingId: listing.id,
          topicKey: topic,
          sendToPreferredAgent: selectedAgentEmail !== listing.agent?.email
        }
      })

      toast({
        title: intl.formatMessage(MSG.messageSent),
        status: 'success'
      })

      if (isVerificationFlowEnabled) {
        // Start verification flow for LO prequal after RE message success
        startVerification(
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage,
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey,
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey, // Using same topic for both verified and default
          'LoanOfficer'
        )
      }
    } catch (err) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          // @ts-ignore
          error: err.message || '',
          // @ts-ignore
          requestData: err.config?.data || {},
          // @ts-ignore
          requestErrors: Object.values(err?.response?.data?.errors || {})
        }
      })
      return dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }
  }

  const agents = useMemo(() => {
    const agentListOptions: AgentOptions[] = [
      {
        type: 'LISTING AGENT',
        email: listing?.agent?.email,
        name: listing?.agent?.name || listing?.agent?.email,
        company: listing?.brokerage?.name
      }
    ]

    if (preferredRealEstateAgentMembership?.customerProfile?.email) {
      agentListOptions.push({
        type: 'YOUR PREFERRED AGENT',
        email: preferredRealEstateAgentMembership?.customerProfile?.email,
        name: `${preferredRealEstateAgentMembership?.customerProfile?.firstName} ${preferredRealEstateAgentMembership?.customerProfile?.lastName}`,
        company: undefined
      })
    }
    if (!hasPreferredAgent) {
      agentListOptions.push({
        type: intl.formatMessage(MSG.addCustomAgent).toUpperCase(),
        email: 'custom',
        name: '',
        company: intl.formatMessage(MSG.addCustomAgentNote)
      })
    }

    return agentListOptions
  }, [listing, preferredRealEstateAgentMembership?.customerProfile]) satisfies AgentOptions[]

  const sendButtonEnabled = useMemo(() => {
    let enabled = false
    if (!emailIsValid) return false
    if (selectedAgentEmail === 'custom') {
      enabled = !!customPreferredAgent.email && !!customPreferredAgent.firstName
    } else {
      enabled = !!selectedAgentEmail
    }
    return enabled
  }, [selectedAgentEmail, customPreferredAgent])

  const validateEmail = () => {
    if (customPreferredAgent.email) {
      const email = customPreferredAgent.email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        setEmailIsValid(false)
        dispatch(flashNotification({ message: 'Invalid email', messageType: 'error' }))
        return
      }
      setEmailIsValid(true)
    }
  }

  if (!listing?.agent?.email) return null

  return (
    <>
      {/* @ts-ignore */}
      <Card size={'lg'} backgroundColor='var(--hb-colors-primary-50) !important'>
        <CardBody p={4}>
          <Flex justify='space-between' align={isHidden ? 'center' : 'flex-start'}>
            <Flex gap={2} align='center'>
              <Box>
                <Heading
                  as='h3'
                  textAlign='left'
                  mb={isHidden ? 0 : 1}
                  size={isHidden ? '3xs' : 'sm'}
                  variant={isHidden ? 'eyebrow' : undefined}
                  fontWeight={isHidden ? 'semibold' : 'bold'}
                  color={isHidden ? 'primary.700' : 'neutral.900'}
                >
                  {intl.formatMessage(MSG.head)}
                </Heading>
                {isHidden && (
                  <Heading as='h4' mb={0} size='3xs'>
                    {intl.formatMessage(MSG.ask, { name: listing?.agent?.name })}
                  </Heading>
                )}
              </Box>
            </Flex>
            <IconButton
              size='xs'
              display={['flex', null, null, 'none']}
              onClick={() => setIsHidden(!isHidden)}
              icon={<Icon name={isHidden ? 'angle-up' : 'angle-down'} />}
              aria-label={intl.formatMessage(isHidden ? MSG.show : MSG.hide)}
              tracking={{
                guid: 'iyatREAj3S72vDVQtTuzc4',
                ui_context: HOME_SEARCH_LISTING_DETAILS,
                descriptor: isHidden ? MSG.show.defaultMessage : MSG.hide.defaultMessage
              }}
            />
          </Flex>
          {!isHidden && (
            <>
              <Text size='xs' id='agent-question-desc'>
                {intl.formatMessage(MSG.sub)}
              </Text>

              <Form
                onSubmit={handleSubmit}
                values={{
                  question: '',
                  preferredAgent: selectedAgentEmail
                }}
              >
                {({ formState: { errors, isValid } }) => (
                  <>
                    <FormControl isInvalid={!!errors?.question} mb={4}>
                      <FormLabel>{intl.formatMessage(MSG.label)}</FormLabel>
                      <Textarea
                        size='xs'
                        name='question'
                        borderRadius={8}
                        color='neutral.900'
                        aria-describedby='agent-question-desc'
                        rules={{ required: intl.formatMessage(MSG.required) }}
                      />
                      <FormErrorMessage>{errors?.question?.message?.toString()}</FormErrorMessage>

                      <Box mt={4}>
                        <Text mb={-4} size='sm' fontWeight={600}>
                          {intl.formatMessage(MSG.whoDoYouWantToAsk)}
                        </Text>
                        <RadioGroup
                          name='preferredAgent'
                          onChange={(event: React.FormEvent<HTMLDivElement>) => {
                            const target = event.target as HTMLInputElement
                            setSelectedAgentEmail(target.value)
                          }}
                        >
                          <Stack direction='column'>
                            {agents.map((agent, i) => (
                              <Box mt={i > 0 ? -8 : 0} key={i}>
                                <Radio value={agent?.email as string}>
                                  <Box pt={8}>
                                    <Text m={0} size='3xs' color='primary'>
                                      {agent?.type}
                                    </Text>
                                    <Text m={0} size='xs'>
                                      {agent?.name}
                                    </Text>
                                    <Text m={0} size='3xs' color='neutral-700'>
                                      {agent?.company ?? '-'}
                                    </Text>
                                  </Box>
                                </Radio>
                              </Box>
                            ))}
                          </Stack>
                        </RadioGroup>

                        {!hasPreferredAgent && (
                          <Box>
                            <Box mt={4}>
                              {selectedAgentEmail === 'custom' && (
                                <Flex flexDirection='column' gap={3}>
                                  <Flex gap={2}>
                                    <Box>
                                      <Flex gap={1}>
                                        <Text m={0} size='xs'>
                                          {intl.formatMessage(MSG.firstName)}
                                        </Text>
                                        <Text m={0} size='xs' color='danger'>
                                          *
                                        </Text>
                                      </Flex>
                                      <Input
                                        value={customPreferredAgent.firstName}
                                        onChange={e => {
                                          setCustomPreferredAgent({
                                            ...customPreferredAgent,
                                            firstName: e.target.value
                                          })
                                        }}
                                        size='sm'
                                        name='firstName'
                                      />
                                    </Box>
                                    <Box>
                                      <Flex gap={1}>
                                        <Text m={0} size='xs'>
                                          {intl.formatMessage(MSG.lastName)}
                                        </Text>
                                        <Text m={0} size='xs' color='danger'>
                                          *
                                        </Text>
                                      </Flex>
                                      <Input
                                        value={customPreferredAgent.lastName}
                                        onChange={e => {
                                          setCustomPreferredAgent({
                                            ...customPreferredAgent,
                                            lastName: e.target.value
                                          })
                                        }}
                                        size='sm'
                                        name='lastName'
                                      />
                                    </Box>
                                  </Flex>
                                  <Box>
                                    <Flex gap={1}>
                                      <Text m={0} size='xs'>
                                        {intl.formatMessage(MSG.email)}
                                      </Text>
                                      <Text m={0} size='xs' color='danger'>
                                        *
                                      </Text>
                                    </Flex>
                                    <Input
                                      isInvalid={!emailIsValid}
                                      type='email'
                                      value={customPreferredAgent.email}
                                      onBlur={validateEmail}
                                      onChange={e => {
                                        setCustomPreferredAgent({
                                          ...customPreferredAgent,
                                          email: e.target.value
                                        })
                                      }}
                                      size='sm'
                                      name='email'
                                    />
                                  </Box>
                                </Flex>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </FormControl>

                    <Button
                      w='100%'
                      type='submit'
                      isDisabled={!sendButtonEnabled || !isValid}
                      isLoading={isSendingListingMessage}
                      colorScheme='primary'
                      leftIcon={<Icon name='message' />}
                      tracking={{
                        guid: 'nG8M7arfPw3NsuN3dfLGMq',
                        ui_context: HOME_SEARCH_LISTING_DETAILS,
                        descriptor: MSG.submit.defaultMessage
                      }}
                    >
                      {intl.formatMessage(MSG.submit)}
                    </Button>
                  </>
                )}
              </Form>
              <Text mb={0} mt={4} size='3xs'>
                {intl.formatMessage(MSG.footer)}
              </Text>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
